import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSharedAI } from '../context/SharedAIContext';
import { CoRegulateLogo, CoRegulateMark } from '../components/Logo';
import { Heart, Users, MessageCircle, History, Settings, ChevronRight, Lock, Sparkles, BookOpen, Bell, Zap, HeartHandshake, Wind, Compass, RefreshCw, Clock, ClipboardCheck, Brain, Bookmark, Star, AlertTriangle, UserPlus, Circle, Send, LogOut, User, Gift, Home, Eye, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { BrandCard, StatsCard } from '../components/brand/BrandCard';
import { Heading, SubHeading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';
import { usePremiumFeature } from '../components/PremiumFeature';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { EmotionReferenceGuide } from '../components/cycle/EmotionReferenceGuide';
import { OnboardingTour, useTourStatus } from '../components/OnboardingTour';
import { useFavorites, TOOL_METADATA } from '../hooks/useFavorites';
import { ProgressCard } from '../components/ProgressCard';
import { RecommendedForYou } from '../components/RecommendedForYou';
import { FoundingMemberCard, FoundingMemberBadge, useFoundingOffer } from '../components/FoundingMember';
import { QuickAccessFavorites } from '../components/QuickAccessFavorites';
import { parseMessageWithActions } from '../utils/actionTags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Guide Hero Component
 * Front and center on the dashboard - the primary way users interact with the app
 * Shares conversation state with AICompanion via SharedAIContext
 */
const AIGuideHero = ({ navigate, userName }) => {
    const { messages, setMessages, pendingNavigation, setNavigation, hasInteracted, sessionId } = useSharedAI();
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showFullChat, setShowFullChat] = useState(false);
    const chatContainerRef = useRef(null);
    
    // Auto-scroll to latest message within the chat container only
    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };
    
    // Show chat if there are existing messages from shared context
    useEffect(() => {
        if (messages.length > 0) {
            setShowFullChat(true);
        }
    }, [messages]);
    
    // Scroll to bottom when messages change or loading state changes
    useEffect(() => {
        // Small delay to ensure DOM has updated
        setTimeout(scrollToBottom, 50);
    }, [messages, isLoading]);
    
    const quickPrompts = [
        { text: "I'm overwhelmed", emoji: "😔" },
        { text: "My mind won't stop", emoji: "🌀" },
        { text: "I need something quick", emoji: "⚡" }
    ];

    // Action routes mapping - Direct tool access
    // NOTE: Settling routes are commented out but preserved for re-enabling later
    const actionRoutes = {
        'go_to_still_sphere': '/regulate/still-sphere',
        'go_to_calm_cycle': '/regulate/breathing-orb',
        'go_to_micro_reset': '/regulate/micro-reset',
        'go_to_box_breathing': '/regulate/box-breathing',
        'go_to_navigator': '/regulate/navigator',
        'go_to_regulation_tools': '/regulate',
        'start_initial_assessment': '/initial-assessment',
        'go_to_check_in': '/initial-assessment',
        'go_to_assessment': '/initial-assessment',
        'start_relationship_assessment': '/relationship-hub/check-in',
        'go_to_partner_support': '/regulate/adhd-partner-support',
        'go_to_body_scan': '/regulate/body-scan',
        'go_to_pmr': '/regulate/pmr',
        'go_to_somatic': '/regulate/somatic',
        'go_to_guided_meditations': '/regulate/guided-meditations',
        'go_to_breathing_patterns': '/regulate/breathing-patterns',
        'go_to_crisis': '/crisis',
        // Specific breathing pattern routes
        'go_to_breathing_physiological_sigh': '/regulate/breathing-patterns?pattern=physiological-sigh',
        'go_to_breathing_478': '/regulate/breathing-patterns?pattern=4-7-8',
        'go_to_breathing_box': '/regulate/breathing-patterns?pattern=box',
        'go_to_breathing_extended_exhale': '/regulate/breathing-patterns?pattern=extended-exhale',
        'go_to_breathing_coherent': '/regulate/breathing-patterns?pattern=coherent',
        // Educational content - Learn More routes (navigate to Understanding ADHD page)
        'learn_more_rsd': '/understanding-adhd',
        'learn_more_emotional_dysregulation': '/understanding-adhd',
        'learn_more_time_blindness': '/understanding-adhd',
        'learn_more_masking': '/understanding-adhd',
        'learn_more_hyperfocus': '/understanding-adhd',
        'learn_more_coregulation': '/understanding-adhd',
        'learn_more_adhd_mental_health': '/understanding-adhd',
        'learn_more_adhd_relationships': '/understanding-adhd',
        'learn_more_attachment_styles': '/understanding-adhd',
        'learn_more_negative_cycles': '/understanding-adhd',
        // Hypnotherapy
        'go_to_hypnotherapy': '/hypnotherapy',
        // DISABLED: Settling routes - uncomment to re-enable
        // 'settle_then_still_sphere': '/settle?next=/regulate/still-sphere&context=tool',
        // 'settle_then_calm_cycle': '/settle?next=/regulate/breathing-orb&context=tool',
        // 'settle_then_box_breathing': '/settle?next=/regulate/box-breathing&context=tool',
        // 'settle_then_navigator': '/settle?next=/regulate/navigator&context=tool',
        // 'settle_then_micro_reset': '/settle?next=/regulate/micro-reset&context=tool',
    };

    // Get friendly name for action
    const getActionName = (action) => {
        const names = {
            'go_to_still_sphere': 'Still Sphere',
            'go_to_calm_cycle': 'Calm Cycle',
            'go_to_micro_reset': 'Micro Reset',
            'go_to_box_breathing': 'Box Breathing',
            'go_to_navigator': 'Navigator',
            'go_to_regulation_tools': 'Regulation Tools',
            'start_initial_assessment': 'Check-In',
            'start_relationship_assessment': 'Relationship Check-In',
            'go_to_partner_support': 'Partner Support',
            'go_to_body_scan': 'Body Scan',
            'go_to_pmr': 'Muscle Relaxation',
            'go_to_somatic': 'Somatic Practices',
            'go_to_guided_meditations': 'Guided Meditations',
            'go_to_breathing_patterns': 'Breathing Patterns',
            'go_to_crisis': 'Crisis Resources',
            // Specific breathing patterns
            'go_to_breathing_physiological_sigh': 'Physiological Sigh (30 sec)',
            'go_to_breathing_478': '4-7-8 Breathing',
            'go_to_breathing_box': 'Box Breathing',
            'go_to_breathing_extended_exhale': 'Extended Exhale',
            'go_to_breathing_coherent': 'Coherent Breathing',
            // Learn More actions
            'learn_more_rsd': 'Learn about RSD',
            'learn_more_emotional_dysregulation': 'Learn about Emotional Dysregulation',
            'learn_more_time_blindness': 'Learn about Time Blindness',
            'learn_more_masking': 'Learn about Masking',
            'learn_more_hyperfocus': 'Learn about Hyperfocus',
            'learn_more_coregulation': 'Learn about Co-Regulation',
            'learn_more_adhd_mental_health': 'Learn about ADHD & Mental Health',
            'learn_more_adhd_relationships': 'Learn about ADHD in Relationships',
            'learn_more_attachment_styles': 'Learn about Attachment Styles',
            'learn_more_negative_cycles': 'Learn about Negative Cycles',
        };
        return names[action] || 'Tool';
    };

    // Track if this is the first message
    const [isFirstMessage, setIsFirstMessage] = useState(true);

    const sendMessage = async (messageText) => {
        if (!messageText.trim() || isLoading) return;
        
        const userMessage = messageText.trim();
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setInput('');
        setIsLoading(true);
        setShowFullChat(true);
        setNavigation(null);

        try {
            const token = localStorage.getItem('co_regulate_token');
            
            // Get user's local hour for time-aware greetings
            const userLocalHour = new Date().getHours();
            
            const response = await fetch(`${API_URL}/api/guide/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    message: userMessage,
                    session_id: sessionId,
                    has_completed_assessment: true,
                    context: 'dashboard',
                    user_local_hour: userLocalHour,
                    is_first_message: isFirstMessage
                })
            });

            // After first message, set flag to false
            if (isFirstMessage) {
                setIsFirstMessage(false);
            }

            if (response.ok) {
                const data = await response.json();
                setMessages(prev => [...prev, { 
                    role: 'assistant', 
                    content: data.response,
                    action: data.action
                }]);

                // Store pending navigation - don't auto-navigate
                if (data.action && actionRoutes[data.action]) {
                    setNavigation({
                        action: data.action,
                        route: actionRoutes[data.action],
                        name: getActionName(data.action)
                    });
                }
            }
        } catch (error) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "I'm here with you. Would you like to try a grounding tool?"
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNavigate = () => {
        if (pendingNavigation) {
            navigate(pendingNavigation.route);
        }
    };

    return (
        <section className="mb-8" data-testid="ai-guide-hero">
            <div className="relative overflow-hidden rounded-3xl glass-card-sage p-4 sm:p-6">
                {/* Decorative elements - subtle floating orbs */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#7D8E95]/15 to-[#7D8E95]/10 rounded-full blur-3xl animate-float" />
                <div className="absolute bottom-0 left-0 w-36 h-36 bg-gradient-to-tr from-[#FFBB98]/12 to-[#7D8E95]/15 rounded-full blur-2xl animate-float" style={{ animationDelay: '2s' }} />
                <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-[#FFBB98]/8 rounded-full blur-xl animate-pulse-soft" />
                
                <div className="relative z-10">
                    {/* Header - AI Welcome Message */}
                    <div className="flex items-center gap-2 sm:gap-3 mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-full bg-gradient-to-br from-[#7D8E95] to-[#1D6B6B] flex items-center justify-center icon-glow-sage">
                            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="min-w-0">
                            <Heading as="h2" size="text-base sm:text-lg" className="text-[#1D6B6B]">
                                Hi{userName ? `, ${userName}` : ''} — I'm Sage
                            </Heading>
                            <SmallText className="text-[#1D6B6B] text-xs sm:text-sm">
                                I'm here to help you find what you need
                            </SmallText>
                        </div>
                    </div>

                    {/* Chat Area */}
                    {showFullChat && messages.length > 0 && (
                        <div className="mb-4">
                            <div 
                                ref={chatContainerRef}
                                className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 max-h-48 overflow-y-auto border border-white/60"
                            >
                                <div className="space-y-4">
                                    {messages.map((msg, idx) => {
                                        const { text: cleanText, actions } = msg.role === 'assistant' 
                                            ? parseMessageWithActions(msg.content)
                                            : { text: msg.content, actions: [] };
                                        
                                        return (
                                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            {msg.role === 'user' ? (
                                                /* User message - simple rounded bubble */
                                                <div className="max-w-[85%] rounded-2xl px-4 py-2.5 bg-[#7D8E95]/25 text-[#1D6B6B] border border-[#7D8E95]/20">
                                                    <BodyText className="leading-relaxed">{msg.content}</BodyText>
                                                </div>
                                            ) : (
                                                /* Assistant message - speech bubble with tail on bottom-right */
                                                <div className="max-w-[85%] relative">
                                                    <div className="rounded-2xl px-4 py-2.5 bg-white/80 text-[#1D6B6B] shadow-soft-sm border border-white/60">
                                                        <BodyText className="leading-relaxed">{cleanText}</BodyText>
                                                        {/* Inline tool links */}
                                                        {actions.length > 0 && (
                                                            <div className="mt-2 pt-1.5 border-t border-[#7D8E95]/10">
                                                                <span className="text-[10px] text-[#7D8E95]/60">Go to: </span>
                                                                {actions.map((action, actionIdx) => (
                                                                    <button
                                                                        key={actionIdx}
                                                                        onClick={() => navigate(action.route)}
                                                                        className="text-xs text-[#1D6B6B] hover:text-[#155555] underline underline-offset-2 mr-2"
                                                                    >
                                                                        {action.name}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Speech bubble tail - bottom right */}
                                                    <div 
                                                        className="absolute -bottom-1.5 right-4 w-3 h-3 bg-white/80 border-r border-b border-white/60 transform rotate-45"
                                                        style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );})}
                                    {isLoading && (
                                        <div className="flex justify-start">
                                            <div className="relative">
                                                <div className="bg-white/80 rounded-2xl px-4 py-2.5 shadow-soft-sm border border-white/60">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" />
                                                        <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '150ms' }} />
                                                        <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                </div>
                                                {/* Speech bubble tail */}
                                                <div 
                                                    className="absolute -bottom-1.5 right-4 w-3 h-3 bg-white/80 border-r border-b border-white/60 transform rotate-45"
                                                    style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* "I just need a tool" escape hatch - shows during conversation without a pending navigation */}
                            {!pendingNavigation && !isLoading && messages.length > 0 && (
                                <div className="mt-2 text-center">
                                    <button
                                        onClick={() => navigate('/regulate')}
                                        className="text-xs text-[#7D8E95] hover:text-[#1D6B6B] transition-colors underline underline-offset-2"
                                        data-testid="skip-to-tools-btn"
                                    >
                                        I just need a tool now →
                                    </button>
                                </div>
                            )}
                            
                            {/* Navigation link removed - now using inline tool links in message text */}
                        </div>
                    )}

                    {/* Input Area */}
                    <div className="flex gap-2 sm:gap-3">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage(input)}
                            placeholder={messages.length > 0 ? "Type a message..." : "How are you feeling right now?"}
                            className="flex-1 min-w-0 px-4 sm:px-5 py-3 sm:py-3.5 rounded-full bg-white/70 backdrop-blur-sm border border-white/40/80 focus:outline-none focus:border-[#7D8E95] focus:ring-2 focus:ring-[#7D8E95]/20 focus:bg-white/90 text-[#1D6B6B] placeholder-[#7D8E95] transition-all shadow-inner-soft text-sm sm:text-base"
                            data-testid="ai-guide-input"
                        />
                        <button
                            onClick={() => sendMessage(input)}
                            disabled={!input.trim() || isLoading}
                            className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#145555] text-white flex items-center justify-center hover:from-[#1D6B6B] hover:to-[#142428] disabled:opacity-50 disabled:hover:from-[#1D6B6B] disabled:hover:to-[#145555] transition-all duration-300 icon-glow-sage"
                            data-testid="ai-guide-send"
                        >
                            <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                    
                    {/* Quick Prompts - Pill/Chip Design */}
                    <div className="mt-3 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
                        <div className="flex gap-2 w-max">
                            {quickPrompts.map((prompt, idx) => {
                                const iconColors = [
                                    'bg-gradient-to-br from-[#FFBB98] to-[#E09060]', // Coral
                                    'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]', // Teal
                                    'bg-gradient-to-br from-[#E8A0A0] to-[#C97B7B]'  // Dusty Rose
                                ];
                                return (
                                    <button
                                        key={idx}
                                        onClick={() => sendMessage(prompt.text)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 hover:bg-white/90 backdrop-blur-sm border border-white/60 hover:border-[#2A8585]/30 text-[#1D6B6B] text-xs font-medium transition-all duration-300 hover:shadow-md active:scale-95 whitespace-nowrap flex-shrink-0"
                                        data-testid={`quick-prompt-${idx}`}
                                    >
                                        <span className={`w-5 h-5 rounded-md ${iconColors[idx]} flex items-center justify-center text-white text-[10px]`}>
                                            {prompt.emoji}
                                        </span>
                                        {prompt.text}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Subtle hint */}
                    <SmallText className="text-center mt-3 text-[#7D8E95]">
                        Tell me how you're feeling and I'll guide you to the right tool
                    </SmallText>
                </div>
            </div>
        </section>
    );
};

/**
 * UnifiedHome - Context-Aware Home Experience
 * 
 * A single, unified home that adapts based on user state:
 * - New users: See a warm welcome with gentle body check-in flow
 * - Returning users: See the full dashboard with tools and progress
 * 
 * This eliminates the confusing navigation between /welcome and /dashboard
 */

const STORAGE_KEY = 'coregulate_hasCompletedOnboarding';
const GLOBAL_RECENT_TOOLS_KEY = 'coregulate_recent_tools';
const LAST_CHECKIN_KEY = 'coregulate_last_checkin_date';
const MAX_RECENT_TOOLS = 3;
const CHECKIN_COOLDOWN_HOURS = 12; // Only show check-in every 12 hours

// Global tool metadata for displaying recent tools across the app
const GLOBAL_TOOL_METADATA = {
    // Regulation tools
    '/regulate/micro-reset': { title: 'Micro Reset', icon: Wind, variant: 'sage' },
    '/regulate/regulation-navigator': { title: 'Regulation Navigator', icon: Compass, variant: 'sage' },
    '/regulate/coregulation': { title: 'Relationship Co-Regulation', icon: Users, variant: 'blue' },
    '/regulate/self-talk': { title: 'Self-Talk Reframes', icon: MessageCircle, variant: 'sage' },
    '/regulate/adhd-triggers': { title: 'ADHD Triggers', icon: Heart, variant: 'lavender' },
    '/regulate/what-do-we-need': { title: 'What Do We Need?', icon: Users, variant: 'blue' },
    '/regulate/partner-mode': { title: 'Real-Time Partner Sync', icon: Users, variant: 'blue' },
    '/regulate/relationship-check': { title: 'Safety Check', icon: Heart, variant: 'rose' },
    // Calm Cycle meditation
    '/regulate/breathing-orb': { title: 'Calm Cycle', icon: Wind, variant: 'lavender' },
    '/butterfly-meditation': { title: 'Calm Cycle', icon: Wind, variant: 'lavender' },
    '/regulate/butterfly-meditation': { title: 'Calm Cycle', icon: Wind, variant: 'lavender' },
    // Still Sphere
    '/regulate/still-sphere': { title: 'Still Sphere', icon: Circle, variant: 'gold' },
    // Relationship Hub tools
    '/relationship-hub': { title: 'Relationship Hub', icon: HeartHandshake, variant: 'rose' },
    '/relationship-hub/solo-need': { title: 'What Do I Need?', icon: Heart, variant: 'lavender' },
    '/relationship-hub/break-the-cycle': { title: 'Break the Cycle', icon: RefreshCw, variant: 'lavender' },
    '/relationship-hub/cycles': { title: 'Which Cycle Are We In?', icon: RefreshCw, variant: 'lavender' },
    '/relationship-hub/attachment-needs': { title: 'Attachment Needs', icon: Heart, variant: 'rose' },
    '/relationship-hub/check-in': { title: 'Relationship Check-in', icon: ClipboardCheck, variant: 'lavender' },
    // Main paths
    '/regulate': { title: 'Regulation Tools', icon: Heart, variant: 'sage' },
    '/coregulate': { title: 'Co-Regulate', icon: Users, variant: 'lavender' },
    '/communicate': { title: 'Communication Scripts', icon: MessageCircle, variant: 'blue' }
};

// Helper to track tool usage globally
export const trackGlobalToolUsage = (path) => {
    const stored = localStorage.getItem(GLOBAL_RECENT_TOOLS_KEY);
    let recent = [];
    if (stored) {
        try {
            recent = JSON.parse(stored);
        } catch (e) {
            recent = [];
        }
    }
    // Remove if already exists (to move to front)
    recent = recent.filter(p => p !== path);
    // Add to front
    recent.unshift(path);
    // Keep only MAX_RECENT_TOOLS
    recent = recent.slice(0, MAX_RECENT_TOOLS);
    localStorage.setItem(GLOBAL_RECENT_TOOLS_KEY, JSON.stringify(recent));
    return recent;
};

// Helper to get recent tools
export const getGlobalRecentTools = () => {
    const stored = localStorage.getItem(GLOBAL_RECENT_TOOLS_KEY);
    if (stored) {
        try {
            return JSON.parse(stored).slice(0, MAX_RECENT_TOOLS);
        } catch (e) {
            return [];
        }
    }
    return [];
};

const UnifiedHome = () => {
    const navigate = useNavigate();
    const { user, api, canAccess, subscription, logout } = useAuth();
    const { checkAccess } = usePremiumFeature();
    const { shouldShowTour, isLoading: tourStatusLoading, completeTour } = useTourStatus();
    const { favorites, toggle: toggleFavorite, isFavorite, getFavoritesWithMetadata } = useFavorites();
    
    // User state
    const [isNewUser, setIsNewUser] = useState(null); // null = loading
    const [showWelcomeFlow, setShowWelcomeFlow] = useState(false);
    const [showTour, setShowTour] = useState(false);
    
    // Welcome flow state
    const [step, setStep] = useState('body'); // body, emotions, direction, recommendation (removed welcome step)
    const [bodyState, setBodyState] = useState(null);
    const [selectedEmotions, setSelectedEmotions] = useState([]);
    const [expandedEmotion, setExpandedEmotion] = useState(null);
    const [direction, setDirection] = useState(null);
    const [fadeIn, setFadeIn] = useState(false);
    
    // Dashboard state
    const [stats, setStats] = useState(null);
    const [recentSessions, setRecentSessions] = useState([]);
    const [recentTools, setRecentTools] = useState([]);
    
    // Check for URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const skipOnboarding = urlParams.get('skip') === 'true';
    const tabParam = urlParams.get('tab');
    
    // Set initial tab based on URL parameter
    const [activeTab, setActiveTab] = useState(tabParam === 'relationships' ? 'relationships' : 'regulation');

    // Check user status on mount
    useEffect(() => {
        // Wait for tour status to load before deciding whether to show tour
        if (tourStatusLoading) return;
        
        const hasCompletedOnboarding = localStorage.getItem(STORAGE_KEY) === 'true';
        const hasCheckedInThisSession = sessionStorage.getItem('body_checkin_completed') === 'true';
        
        // Check if enough time has passed since last check-in (cooldown period)
        const lastCheckinTime = localStorage.getItem(LAST_CHECKIN_KEY);
        const now = Date.now();
        const cooldownMs = CHECKIN_COOLDOWN_HOURS * 60 * 60 * 1000;
        const isWithinCooldown = lastCheckinTime && (now - parseInt(lastCheckinTime, 10)) < cooldownMs;
        
        // If skip parameter is present, show dashboard regardless
        if (skipOnboarding) {
            setIsNewUser(false);
            setShowWelcomeFlow(false);
            // Show tour if not completed
            if (shouldShowTour) {
                setTimeout(() => setShowTour(true), 500);
            }
        } else if (hasCheckedInThisSession || isWithinCooldown) {
            // Already checked in this session OR within cooldown period - go to dashboard
            setIsNewUser(false);
            setShowWelcomeFlow(false);
        } else {
            // Show body check-in (either new user or cooldown expired)
            setIsNewUser(!hasCompletedOnboarding);
            setShowWelcomeFlow(true);
        }
        setRecentTools(getGlobalRecentTools());
        setTimeout(() => setFadeIn(true), 100);
    }, [skipOnboarding, shouldShowTour, tourStatusLoading]);

    // Fetch dashboard data for returning users
    useEffect(() => {
        if (isNewUser === false) {
            const fetchData = async () => {
                try {
                    const [statsRes, sessionsRes] = await Promise.all([
                        api().get('/stats/summary'),
                        api().get('/regulation/sessions?limit=3')
                    ]);
                    setStats(statsRes.data);
                    setRecentSessions(sessionsRes.data);
                } catch (error) {
                    console.error('Failed to fetch dashboard data:', error);
                }
            };
            fetchData();
        }
    }, [api, isNewUser]);

    // Reset fade for step transitions
    useEffect(() => {
        setFadeIn(false);
        const timer = setTimeout(() => setFadeIn(true), 50);
        return () => clearTimeout(timer);
    }, [step]);

    // Mark onboarding as complete
    const completeOnboarding = () => {
        localStorage.setItem(STORAGE_KEY, 'true');
        localStorage.setItem(LAST_CHECKIN_KEY, Date.now().toString()); // Save check-in timestamp for cooldown
        sessionStorage.setItem('body_checkin_completed', 'true'); // Mark this session as checked in
        setIsNewUser(false);
        setShowWelcomeFlow(false);
        // Show tour after a brief delay
        if (shouldShowTour) {
            setTimeout(() => setShowTour(true), 800);
        }
    };

    // Handle favorite toggle
    const handleFavoriteToggle = (e, path) => {
        e.stopPropagation(); // Prevent card click
        const added = toggleFavorite(path);
        toast.success(added ? 'Added to favorites' : 'Removed from favorites', {
            duration: 2000
        });
    };

    // Body state options - activation states for Calm Cycle, overwhelm/shutdown for Still Sphere
    const bodyOptions = [
        { id: 'tense', label: 'Tense', description: 'Tight shoulders, jaw, or chest', color: 'bg-[#FFBB98]/30' },
        { id: 'wound-up', label: 'Wound-up', description: 'Racing thoughts, can\'t slow down', color: 'bg-[#E4D3A3]/40' },
        { id: 'overwhelmed', label: 'Overwhelmed', description: 'Everything feels like too much', color: 'bg-[#7D8E95]/20' },
        { id: 'flat', label: 'Flat / Foggy', description: 'Low energy, numb, disconnected', color: 'bg-[#D4D4CF]/40' },
        { id: 'unsure', label: 'Not sure', description: 'Hard to tell right now', color: 'bg-[#1D6B6B]/10' }
    ];

    // Direction options - using glass-card variants for consistency
    const directionOptions = [
        { id: 'calm', label: 'Calm me', description: 'Slow down, find stillness', variant: 'sage' },
        { id: 'ground', label: 'Ground me', description: 'Feel more present, stable', variant: 'lavender' },
        { id: 'stillness', label: 'Give me stillness', description: 'Quiet, warmth, no demands', variant: 'rose' },
        { id: 'focus', label: 'Help me focus', description: 'Clear the mental noise', variant: 'blue' },
        { id: 'unsure', label: 'Not sure', description: 'Just guide me gently', variant: 'gold' }
    ];

    // Emotion-aware tool recommendations
    const toolDefinitions = {
        'regulation-navigator': {
            title: 'Regulation Navigator',
            path: '/regulate/regulation-navigator',
            // Emotions that strongly suggest this tool
            emotionBoost: ['scared', 'overwhelmed', 'ashamed'],
            descriptions: {
                default: 'A guided path through the overwhelm, one gentle step at a time.',
                scared: 'When anxiety feels big, grounding helps your nervous system feel safe.',
                overwhelmed: 'When everything feels like too much, this is a good place to start.',
                ashamed: 'Gentle grounding to help you reconnect with self-compassion.',
                tense: 'Grounding exercises to help your body feel safe and stable.',
            }
        },
        'micro-reset': {
            title: 'Micro Reset',
            path: '/regulate/micro-reset',
            emotionBoost: ['mad', 'guilty', 'numb'],
            descriptions: {
                default: 'A quick, gentle reset to release and recalibrate.',
                mad: 'A quick reset to release tension and find your center.',
                guilty: 'A gentle pause to soften the weight you\'re carrying.',
                numb: 'Small movements to help you feel present in your body again.',
                tense: 'Release the tension first, then clarity will follow.',
                flat: 'A gentle spark to help bring you back online.',
            }
        },
        'coregulation': {
            title: 'Co-Regulation',
            path: '/regulate/coregulation',
            emotionBoost: ['sad', 'loving'],
            descriptions: {
                default: 'Gentle activation to help you reconnect with yourself.',
                sad: 'When loneliness feels heavy, connection (even with yourself) helps.',
                loving: 'Channel that warmth into a moment of self-care.',
                flat: 'Gentle activation to help you reconnect with yourself.',
            }
        },
        'self-talk': {
            title: 'Self-Talk Reframes',
            path: '/regulate/self-talk',
            emotionBoost: ['ashamed', 'guilty', 'sad'],
            descriptions: {
                default: 'Gentle reframes to shift your inner dialogue.',
                ashamed: 'When the inner critic is loud, softer words can help.',
                guilty: 'Reframe the weight of guilt with self-compassion.',
                sad: 'Words of comfort when you need tenderness.',
            }
        },
        'relationship-hub': {
            title: 'Relationship Hub',
            path: '/relationship-hub',
            emotionBoost: ['loving', 'sad', 'scared'],
            descriptions: {
                default: 'Tools for connection, repair, and relational healing.',
                loving: 'Channel your connection energy into relational tools.',
                sad: 'When you\'re longing for connection, these tools can help.',
                scared: 'Explore attachment needs and find security.',
            }
        },
        'calm-cycle': {
            title: 'Calm Cycle',
            path: '/regulate/breathing-orb',
            // Best for activation, overwhelm, mental spin-up
            emotionBoost: ['overwhelmed', 'scared', 'anxious'],
            descriptions: {
                default: 'A slow, steady rhythm to help your system settle and naturally downshift.',
                overwhelmed: 'When everything is moving too fast, this steady rhythm helps you settle.',
                scared: 'Gentle pacing to ease anxious activation and racing thoughts.',
                tense: 'A steady rhythm to release the wound-up feeling and find calm.',
                activated: 'When you\'re keyed up and can\'t slow down, this helps your system settle.',
                spinning: 'Quiet the mental spin-up with something steady to lean into.',
            }
        },
        'still-sphere': {
            title: 'Still Sphere',
            path: '/regulate/still-sphere',
            // Best for overwhelm, shutdown, flooding - needs stillness not rhythm
            emotionBoost: ['numb', 'sad', 'tired', 'overwhelmed', 'scared'],
            descriptions: {
                default: 'A warm, quiet presence when everything feels like too much.',
                numb: 'When you need warmth without demands, just rest here.',
                sad: 'A gentle, grounding presence when you need quiet stillness.',
                tired: 'No effort needed. Just rest your gaze and let the stillness meet you.',
                overwhelmed: 'When there\'s too much input, stillness helps you settle.',
                scared: 'A quiet, containing presence when you need space around you.',
                flooded: 'When you\'re overloaded, this warmth asks nothing of you.',
            }
        }
    };

    // Get recommendation based on body state, direction, AND selected emotions
    const getRecommendation = () => {
        // Base recommendations from body + direction
        // Calm Cycle = activation, wound-up, racing thoughts (needs rhythm to follow)
        // Still Sphere = overwhelm, shutdown, flooded (needs stillness, no rhythm)
        const baseRecommendations = {
            // Tense - usually activation, Calm Cycle helps
            'tense-calm': 'calm-cycle',
            'tense-ground': 'regulation-navigator',
            'tense-stillness': 'still-sphere',
            'tense-focus': 'micro-reset',
            'tense-unsure': 'calm-cycle',
            // Wound-up - activation state, Calm Cycle rhythm helps
            'wound-up-calm': 'calm-cycle',
            'wound-up-ground': 'calm-cycle',
            'wound-up-stillness': 'calm-cycle',
            'wound-up-focus': 'calm-cycle',
            'wound-up-unsure': 'calm-cycle',
            // Overwhelmed - too much input, Still Sphere stillness helps
            'overwhelmed-calm': 'still-sphere',
            'overwhelmed-ground': 'still-sphere',
            'overwhelmed-stillness': 'still-sphere',
            'overwhelmed-focus': 'micro-reset',
            'overwhelmed-unsure': 'still-sphere',
            // Flat/Foggy - shutdown state, Still Sphere warmth helps
            'flat-calm': 'still-sphere',
            'flat-ground': 'still-sphere',
            'flat-stillness': 'still-sphere',
            'flat-focus': 'micro-reset',
            'flat-unsure': 'still-sphere',
            // Unsure - default to Still Sphere for gentleness
            'unsure-calm': 'still-sphere',
            'unsure-ground': 'regulation-navigator',
            'unsure-stillness': 'still-sphere',
            'unsure-focus': 'micro-reset',
            'unsure-unsure': 'regulation-navigator'
        };

        const key = `${bodyState}-${direction}`;
        let recommendedTool = baseRecommendations[key] || 'micro-reset';
        
        // If user selected emotions, factor them in
        if (selectedEmotions.length > 0) {
            // Count how many times each tool is "boosted" by selected emotions
            const toolScores = {};
            
            Object.entries(toolDefinitions).forEach(([toolId, tool]) => {
                toolScores[toolId] = 0;
                selectedEmotions.forEach(emotion => {
                    if (tool.emotionBoost.includes(emotion)) {
                        toolScores[toolId] += 1;
                    }
                });
            });

            // Find the highest scoring tool from emotions
            const maxScore = Math.max(...Object.values(toolScores));
            
            // Only override if emotions strongly suggest a different tool (2+ matches)
            // OR if base tool has 0 matches and another has 1+
            if (maxScore >= 2) {
                const topTools = Object.entries(toolScores)
                    .filter(([_, score]) => score === maxScore)
                    .map(([toolId]) => toolId);
                
                // Prefer the base recommendation if it's among the top
                if (topTools.includes(recommendedTool)) {
                    // Keep base recommendation
                } else {
                    recommendedTool = topTools[0];
                }
            } else if (maxScore >= 1 && toolScores[recommendedTool] === 0) {
                // Base tool has no emotion match, but another does - consider switching
                const emotionMatchedTool = Object.entries(toolScores)
                    .find(([_, score]) => score >= 1)?.[0];
                if (emotionMatchedTool) {
                    recommendedTool = emotionMatchedTool;
                }
            }
        }

        // Get the tool definition
        const tool = toolDefinitions[recommendedTool];
        
        // Find the most relevant description based on selected emotions or body state
        let description = tool.descriptions.default;
        
        // Check if any selected emotion has a specific description
        for (const emotion of selectedEmotions) {
            if (tool.descriptions[emotion]) {
                description = tool.descriptions[emotion];
                break;
            }
        }
        
        // Fall back to body state description if no emotion match
        if (description === tool.descriptions.default && tool.descriptions[bodyState]) {
            description = tool.descriptions[bodyState];
        }

        return {
            tool: recommendedTool,
            title: tool.title,
            description,
            path: tool.path
        };
    };

    const handleBodySelect = (id) => {
        setBodyState(id);
        setTimeout(() => setStep('emotions'), 300);
    };

    const handleEmotionSelect = (emotionId) => {
        setSelectedEmotions(prev => {
            if (prev.includes(emotionId)) {
                return prev.filter(id => id !== emotionId);
            }
            // Allow up to 3 selections
            if (prev.length >= 3) {
                return prev;
            }
            return [...prev, emotionId];
        });
    };

    const handleEmotionsContinue = () => {
        setTimeout(() => setStep('direction'), 300);
    };

    const handleDirectionSelect = (id) => {
        setDirection(id);
        setTimeout(() => setStep('recommendation'), 300);
    };

    const handleStartTool = () => {
        const rec = getRecommendation();
        completeOnboarding();
        trackGlobalToolUsage(rec.path);
        navigate(rec.path);
    };

    const handleSkipToDashboard = () => {
        completeOnboarding();
    };

    const recommendation = bodyState && direction ? getRecommendation() : null;

    // Dashboard quick actions - ADHD Regulation Hub
    const regulationActions = [
        { id: 'regulate', icon: Heart, title: 'Regulation Tools', description: 'Individual regulating tools for ADHD brains', path: '/regulate', variant: 'sage' },
        { id: 'emotion-identifier', icon: Sparkles, title: 'Making Sense of Your Emotions', description: 'Name what you\'re feeling and find what helps', path: '/regulate/emotion-identifier', variant: 'lavender' },
        { id: 'navigator', icon: Compass, title: 'Regulation Navigator', description: 'Find the right tool for how you feel', path: '/regulate/regulation-navigator', variant: 'blue' },
        { id: 'guided-meditations', icon: MessageCircle, title: 'Guided Meditations', description: 'Voice-guided sessions for different needs', path: '/regulate/guided-meditations', variant: 'rose', highlight: true },
        { id: 'emdr', icon: Eye, title: 'EMDR/Bilateral Regulation', description: 'Rhythmic, alternating sensory input to calm & ground', path: '/regulate/emdr', variant: 'lavender', highlight: true },
        { id: 'breathing-patterns', icon: Wind, title: 'Breathing Patterns', description: '5 different patterns for different needs', path: '/regulate/breathing-patterns', variant: 'gold' },
        { id: 'body-scan', icon: Heart, title: 'Body Scan', description: 'Guided body awareness meditation', path: '/regulate/body-scan', variant: 'sage' },
        { id: 'pmr', icon: Zap, title: 'Progressive Muscle Relaxation', description: 'Tension-release for physical calm', path: '/regulate/pmr', variant: 'lavender' },
        { id: 'somatic', icon: Sparkles, title: 'Somatic Practices', description: 'Body-based emotional processing', path: '/regulate/somatic', variant: 'blue' },
        { id: 'calm-cycle', icon: Heart, title: 'Calm Cycle', description: 'A steady rhythm to settle your system', path: '/regulate/breathing-orb', variant: 'rose' },
        { id: 'still-sphere', icon: Circle, title: 'Still Sphere', description: 'A warm, quiet presence for stillness', path: '/regulate/still-sphere', variant: 'gold' },
        { id: 'micro-reset', icon: Zap, title: 'Micro Reset', description: 'Quick 60-second reset', path: '/regulate/micro-reset', variant: 'sage' },
        { id: 'self-talk', icon: MessageCircle, title: 'Self-Talk Reframes', description: 'Gentle words to shift inner narratives', path: '/regulate/self-talk', variant: 'lavender' },
        { id: 'saved', icon: Bookmark, title: 'Saved Favourites', description: 'Your saved tools and reframes', path: '/regulate/saved', variant: 'gold' },
        { id: 'assessment', icon: ClipboardCheck, title: 'Retake Assessment', description: 'Update your preferences and needs', path: '/initial-assessment', variant: 'rose' }
    ];

    // Dashboard quick actions - ADHD Relationships Hub
    const relationshipActions = [
        { id: 'partner-support', icon: Heart, title: 'For Partners', description: 'Understanding & supporting your ADHD loved one', path: '/regulate/adhd-partner-support', variant: 'rose', highlight: true },
        { id: 'relationship-checkin', icon: ClipboardCheck, title: 'Relationship Check-In', description: 'Guided assessment to find the right tools', path: '/relationship-hub/check-in', variant: 'sage' },
        { id: 'adhd-relationships', icon: Brain, title: 'ADHD & Relationships', description: 'Understanding how ADHD affects your relationships', path: '/adhd-relationships', variant: 'lavender' },
        { id: 'coregulate', icon: Users, title: 'Co-Regulation', description: 'Regulate together with your partner', path: '/coregulate', variant: 'blue' },
        { id: 'communicate', icon: MessageCircle, title: 'Communication Scripts', description: 'Words for difficult conversations', path: '/communicate', variant: 'gold' },
        { id: 'repair', icon: RefreshCw, title: 'Repair & Reconnection', description: 'Conversation guides for reconnecting', path: '/relationship-hub/repair', variant: 'rose' },
        { id: 'browse-tools', icon: HeartHandshake, title: 'Browse All Tools', description: 'View all relationship tools', path: '/relationship-hub?browse=true', variant: 'sage' }
    ];

    const premiumTools = [
        { id: 'reminders', icon: Bell, title: 'Calming Reminders', description: 'Gentle check-in nudges', requiredFeature: 'reminders', tier: 'Support', variant: 'gold' },
        { id: 'micro_tools', icon: Zap, title: 'Micro-Tools', description: 'Quick pause & repair scripts', requiredFeature: 'micro_tools', tier: 'Support', variant: 'lavender' }
    ];

    const handleActionClick = (action) => {
        // Navigate directly - no premium check needed
        trackGlobalToolUsage(action.path);
        setRecentTools(getGlobalRecentTools());
        navigate(action.path);
    };

    const handleRecentToolClick = (path) => {
        trackGlobalToolUsage(path);
        setRecentTools(getGlobalRecentTools());
        navigate(path);
    };

    const handlePremiumToolClick = (tool) => {
        // Navigate directly - no premium check needed
        navigate(tool.path);
    };

    const formatOverwhelmType = (type) => type?.charAt(0).toUpperCase() + type?.slice(1) || 'Unknown';
    const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    // Loading state
    if (isNewUser === null) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-[#7D8E95] animate-breathe" />
            </div>
        );
    }

    // WELCOME FLOW (New Users)
    if (showWelcomeFlow) {
        return (
            <div className="min-h-screen bg-transparent">
                <main className="max-w-md mx-auto px-6 py-12 min-h-screen flex flex-col">
                    
                    {/* BODY CHECK-IN - Now with personalized welcome */}
                    {step === 'body' && (
                        <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                            {/* Personalized Welcome */}
                            <div className="text-center mb-8 pt-6">
                                <SmallText className="text-[#7D8E95] mb-2">
                                    {new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening'}
                                    {user?.name ? ',' : ''}
                                </SmallText>
                                <Heading level={1} className="text-[#1D6B6B] text-2xl md:text-3xl font-medium mb-3 leading-relaxed">
                                    {user?.name ? `Welcome back, ${user.name.split(' ')[0]}` : 'Welcome back'}
                                </Heading>
                                <BodyText className="text-[#7D8E95] text-lg leading-relaxed">
                                    Let's check in with your body
                                </BodyText>
                            </div>
                            
                            {/* Progress dots */}
                            <div className="flex justify-center gap-2 mb-6">
                                <div className="w-8 h-1 rounded-full bg-[#1D6B6B]" />
                                <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                                <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                            </div>
                            
                            {/* Body state question */}
                            <div className="text-center mb-6">
                                <SmallText className="text-[#1D6B6B] font-medium">
                                    How does your body feel right now?
                                </SmallText>
                            </div>
                            
                            <div className="space-y-4 flex-1">
                                {bodyOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        onClick={() => handleBodySelect(option.id)}
                                        className={`w-full p-5 rounded-2xl ${option.color} text-left transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] border border-[#1D6B6B]/10 hover:border-[#1D6B6B]/30 shadow-sm hover:shadow-md`}
                                        data-testid={`body-${option.id}`}
                                    >
                                        <div className="font-medium text-[#1D6B6B] text-lg mb-1">{option.label}</div>
                                        <div className="text-[#7D8E95] text-sm">{option.description}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="mt-6 text-center">
                                <button 
                                    onClick={handleSkipToDashboard} 
                                    className="flex items-center gap-2 mx-auto text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                                    data-testid="skip-to-dashboard-from-body"
                                >
                                    <Home className="w-4 h-4" />
                                    Go straight to dashboard
                                </button>
                            </div>
                        </div>
                    )}

                    {/* EMOTIONS - Identify what you're feeling */}
                    {step === 'emotions' && (
                        <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                            <div className="flex justify-center gap-2 mb-8 pt-4">
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                                <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                            </div>
                            <div className="text-center mb-6">
                                <SmallText className="text-[#1D6B6B] font-medium mb-3">Identify your feelings</SmallText>
                                <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed mb-2">
                                    What emotions feel present?
                                </Heading>
                                <BodyText className="text-[#7D8E95] text-sm">
                                    Select up to 3 that resonate. Tap "Learn more" to explore each one.
                                </BodyText>
                            </div>
                            <div className="flex-1 overflow-y-auto pb-4" style={{ maxHeight: 'calc(100vh - 340px)' }}>
                                <EmotionReferenceGuide
                                    selectedEmotions={selectedEmotions}
                                    onEmotionSelect={handleEmotionSelect}
                                    expandedEmotion={expandedEmotion}
                                    onExpandEmotion={setExpandedEmotion}
                                />
                            </div>
                            <BottomBarSpacer height="extended" />
                        </div>
                    )}
                    
                    {step === 'emotions' && (
                        <PersistentBottomBar>
                            <PrimaryButton 
                                onClick={handleEmotionsContinue} 
                                className="w-full" 
                                size="action" 
                                data-testid="emotions-continue-btn"
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-1.5" />
                            </PrimaryButton>
                            <div className="text-center mt-1.5 space-y-1">
                                <button 
                                    onClick={() => setStep('body')} 
                                    className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors block w-full"
                                >
                                    Back
                                </button>
                                <button 
                                    onClick={handleSkipToDashboard} 
                                    className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors block w-full"
                                >
                                    Skip to dashboard
                                </button>
                            </div>
                        </PersistentBottomBar>
                    )}

                    {/* DIRECTION PROMPT */}
                    {step === 'direction' && (
                        <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                            <div className="flex justify-center gap-2 mb-8 pt-4">
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                            </div>
                            <div className="text-center mb-8">
                                <SmallText className="text-[#1D6B6B] font-medium mb-3">What feels right?</SmallText>
                                <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed">
                                    What would help most right now?
                                </Heading>
                            </div>
                            <div className="space-y-4 flex-1">
                                {directionOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        onClick={() => handleDirectionSelect(option.id)}
                                        className={`w-full p-5 rounded-2xl glass-card-${option.variant} text-left card-hover hover-shine transition-all duration-200`}
                                        data-testid={`direction-${option.id}`}
                                    >
                                        <div className="font-semibold text-[#1D6B6B] text-lg mb-1">{option.label}</div>
                                        <div className="text-[#2A8585] text-sm">{option.description}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="mt-6 text-center">
                                <button onClick={() => setStep('emotions')} className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors">Back</button>
                            </div>
                        </div>
                    )}

                    {/* RECOMMENDATION */}
                    {step === 'recommendation' && recommendation && (
                        <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                            <div className="flex justify-center gap-2 mb-8 pt-4">
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                                <div className="w-8 h-1 rounded-full bg-[#7D8E95]" />
                            </div>
                            <div className="flex-1 flex flex-col justify-center">
                                <div className="text-center mb-6">
                                    <SmallText className="text-[#1D6B6B] font-medium mb-3">Here's what might help</SmallText>
                                </div>
                                <div className="glass-card rounded-2xl p-5">
                                    <div className="w-16 h-16 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-6">
                                        <div className="w-10 h-10 rounded-full bg-[#7D8E95]/40 flex items-center justify-center">
                                            <Sparkles className="w-5 h-5 text-[#1D6B6B]" />
                                        </div>
                                    </div>
                                    <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium mb-3">{recommendation.title}</Heading>
                                    <BodyText className="text-[#7D8E95] leading-relaxed">{recommendation.description}</BodyText>
                                </div>
                                <BottomBarSpacer height="extended" />
                            </div>
                        </div>
                    )}
                    
                    {step === 'recommendation' && recommendation && (
                        <PersistentBottomBar>
                            <PrimaryButton onClick={handleStartTool} className="w-full" size="action" data-testid="start-tool-btn">
                                Let's begin
                                <ChevronRight className="w-4 h-4 ml-1.5" />
                            </PrimaryButton>
                            <div className="text-center mt-1 space-y-1">
                                <button onClick={() => setStep('body')} className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors block w-full">
                                    Try different options
                                </button>
                                <button onClick={handleSkipToDashboard} className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors block w-full" data-testid="browse-all-tools-btn">
                                    Browse all tools
                                </button>
                            </div>
                        </PersistentBottomBar>
                    )}
                </main>
            </div>
        );
    }

    // DASHBOARD (Returning Users)
    
    // Dynamic greeting based on time of day
    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return { greeting: 'Good morning', emoji: '🌅' };
        if (hour < 17) return { greeting: 'Good afternoon', emoji: '☀️' };
        if (hour < 21) return { greeting: 'Good evening', emoji: '🌆' };
        return { greeting: 'Hey there', emoji: '🌙' };
    };
    
    // Contextual subheading based on state
    const getContextualMessage = () => {
        const hour = new Date().getHours();
        const messages = {
            morning: [
                "How are you starting your day?",
                "What's on your mind this morning?",
                "Ready to check in?"
            ],
            afternoon: [
                "How's your day going?",
                "Need a moment to reset?",
                "What's present for you right now?"
            ],
            evening: [
                "How are you winding down?",
                "Ready to settle in?",
                "What do you need right now?"
            ],
            night: [
                "Still up? I'm here.",
                "Can't sleep? Let's chat.",
                "Need some quiet support?"
            ]
        };
        
        let timeOfDay = 'afternoon';
        if (hour < 12) timeOfDay = 'morning';
        else if (hour >= 21) timeOfDay = 'night';
        else if (hour >= 17) timeOfDay = 'evening';
        
        const options = messages[timeOfDay];
        return options[Math.floor(Math.random() * options.length)];
    };
    
    const timeGreeting = getTimeBasedGreeting();
    const contextMessage = getContextualMessage();
    
    return (
        <div className="min-h-screen bg-transparent">
            {/* Header - Clean navigation with CoRegulate branding */}
            <header className="sticky top-0 z-50 glass-header">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
                    {/* Top row - Logo and navigation */}
                    <div className="flex items-center justify-between">
                        {/* Logo - Text only */}
                        <CoRegulateLogo size="lg" />
                        
                        {/* Navigation Menu */}
                        <nav className="flex items-center gap-1 sm:gap-2">
                            <button
                                onClick={() => navigate('/understanding-adhd')}
                                className="px-3 py-2 rounded-lg text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/20 transition-all duration-200"
                                data-testid="nav-learn"
                            >
                                <div className="flex items-center gap-1.5">
                                    <BookOpen className="w-4 h-4" />
                                    <span className="hidden sm:inline">Learn</span>
                                </div>
                            </button>
                            <button
                                onClick={() => navigate('/history')}
                                className="px-3 py-2 rounded-lg text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#7D8E95]/10 transition-all duration-200"
                                data-testid="nav-history"
                            >
                                <div className="flex items-center gap-1.5">
                                    <History className="w-4 h-4" />
                                    <span className="hidden sm:inline">History</span>
                                </div>
                            </button>
                            <button
                                onClick={() => navigate('/settings')}
                                className="px-3 py-2 rounded-lg text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#7D8E95]/10 transition-all duration-200"
                                data-testid="nav-settings"
                            >
                                <div className="flex items-center gap-1.5">
                                    <Settings className="w-4 h-4" />
                                    <span className="hidden sm:inline">Settings</span>
                                </div>
                            </button>
                            <button
                                onClick={() => navigate('/account')}
                                className="px-3 py-2 rounded-lg text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#7D8E95]/10 transition-all duration-200"
                                data-testid="nav-account"
                            >
                                <div className="flex items-center gap-1.5">
                                    <User className="w-4 h-4" />
                                    <span className="hidden sm:inline">Account</span>
                                </div>
                            </button>
                            <button
                                onClick={() => {
                                    logout();
                                    navigate('/landing');
                                }}
                                className="px-3 py-2 rounded-lg text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#7D8E95]/10 transition-all duration-200"
                                data-testid="nav-logout"
                            >
                                <div className="flex items-center gap-1.5">
                                    <LogOut className="w-4 h-4" />
                                    <span className="hidden sm:inline">Logout</span>
                                </div>
                            </button>
                        </nav>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {/* Inspirational Quote */}
                <div className="text-center mb-6 px-4">
                    <p className="text-[#7D8E95] italic text-sm sm:text-base leading-relaxed">
                        "Between stimulus and response there is a space. In that space is our power to choose our response."
                    </p>
                    <p className="text-[#7D8E95] text-xs mt-1">— Viktor Frankl</p>
                </div>
                
                {/* AI Guide Hero Section - Front and Center */}
                <AIGuideHero navigate={navigate} userName={user?.name?.split(' ')[0]} />

                {/* Quick Access Favorites - One-tap access to favorite tools */}
                <QuickAccessFavorites />

                {/* Hub Tabs - More Dynamic */}
                <section className="mb-8">
                    {/* Tab Buttons - Glass-morphism Design with gradient active state */}
                    <div className="glass-card p-1.5 rounded-2xl mb-6 overflow-hidden">
                        <div className="flex gap-1">
                            <button
                                onClick={() => setActiveTab('regulation')}
                                className={`flex-1 py-3 px-3 sm:px-5 rounded-xl font-medium transition-all duration-300 ${
                                    activeTab === 'regulation'
                                        ? 'text-white shadow-md'
                                        : 'text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-white/40'
                                }`}
                                style={activeTab === 'regulation' ? {
                                    background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 50%, #145555 100%)',
                                    boxShadow: '0 4px 15px rgba(52,70,72,0.3)'
                                } : {}}
                                data-testid="tab-regulation"
                            >
                                <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                                    <Heart className={`w-4 h-4 flex-shrink-0 transition-colors ${activeTab === 'regulation' ? 'text-white' : ''}`} />
                                    <span className="text-sm sm:text-base">Regulation</span>
                                </div>
                            </button>
                            <button
                                onClick={() => setActiveTab('relationships')}
                                className={`flex-1 py-3 px-3 sm:px-5 rounded-xl font-medium transition-all duration-300 ${
                                    activeTab === 'relationships'
                                        ? 'text-[#1D6B6B] shadow-md'
                                        : 'text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-white/40'
                                }`}
                                style={activeTab === 'relationships' ? {
                                    background: 'linear-gradient(145deg, #FFD0B0 0%, #FFBB98 50%, #E09060 100%)',
                                    boxShadow: '0 4px 15px rgba(255,187,152,0.35)'
                                } : {}}
                                data-testid="tab-relationships"
                            >
                                <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                                    <HeartHandshake className={`w-4 h-4 flex-shrink-0 transition-colors ${activeTab === 'relationships' ? 'text-[#1D6B6B]' : ''}`} />
                                    <span className="text-sm sm:text-base">Relationships</span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="space-y-4">
                        {/* Relationships Hub Intro - Only show in Relationships tab */}
                        {activeTab === 'relationships' && (
                            <div className="mb-6">
                                {/* Intro text - not in a card */}
                                <div className="text-center mb-6 px-2">
                                    <Heading as="h3" size="text-lg" className="text-[#1D6B6B] mb-2">Relationships & ADHD</Heading>
                                    <BodyText className="text-[#7D8E95] text-sm leading-relaxed">
                                        Tools for connection, communication, and co-regulation. While we often reference romantic partnerships, these work for 
                                        <span className="font-medium text-[#1D6B6B]"> any relationship </span> 
                                        where ADHD plays a role.
                                    </BodyText>
                                </div>

                                {/* Section Label */}
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#FFBB98]"></div>
                                    <SmallText className="text-[#7D8E95] font-medium uppercase tracking-wide text-xs">Relationship Tools</SmallText>
                                </div>
                            </div>
                        )}
                        
                        {/* Regulation Hub Section Label */}
                        {activeTab === 'regulation' && (
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-[#1D6B6B]"></div>
                                <SmallText className="text-[#7D8E95] font-medium uppercase tracking-wide text-xs">Regulation Tools</SmallText>
                            </div>
                        )}

                        {(activeTab === 'regulation' ? regulationActions : relationshipActions).map((action, index) => {
                            // Define glass card styles based on variant - Sunset Lake palette with rich gradients
                            const variantStyles = {
                                sage: {
                                    glassClass: 'glass-card-sage',
                                    iconBg: 'bg-gradient-to-br from-[#2A8585] via-[#1D6B6B] to-[#145555]',
                                    iconGlow: 'icon-glow-sage',
                                    accent: 'text-[#1D6B6B]'
                                },
                                lavender: {
                                    glassClass: 'glass-card-lavender',
                                    iconBg: 'bg-gradient-to-br from-[#9AABB8] via-[#7D8E95] to-[#3A9595]',
                                    iconGlow: 'icon-glow-lavender',
                                    accent: 'text-[#7D8E95]'
                                },
                                rose: {
                                    glassClass: 'glass-card-rose',
                                    iconBg: 'bg-gradient-to-br from-[#FFD0B0] via-[#FFBB98] to-[#E09060]',
                                    iconGlow: 'icon-glow-rose',
                                    accent: 'text-[#FFBB98]'
                                },
                                blue: {
                                    glassClass: 'glass-card-blue',
                                    iconBg: 'bg-gradient-to-br from-[#3A9595] via-[#2A8585] to-[#1D6B6B]',
                                    iconGlow: 'icon-glow-blue',
                                    accent: 'text-[#7D8E95]'
                                },
                                gold: {
                                    glassClass: 'glass-card-gold',
                                    iconBg: 'bg-gradient-to-br from-[#FFD0B0] via-[#FFBB98] to-[#E09060]',
                                    iconGlow: 'icon-glow-gold',
                                    accent: 'text-[#FFBB98]'
                                }
                            };
                            
                            const style = variantStyles[action.variant] || variantStyles.sage;
                            
                            return (
                                <div
                                    key={action.id}
                                    className={`${style.glassClass} rounded-2xl p-5 flex items-center gap-5 cursor-pointer card-hover hover-shine brand-slide-up hover:border-[#7D8E95]/30 ${action.highlight ? 'ring-2 ring-offset-2 ring-[#7D8E95]/30' : ''}`}
                                    style={{ animationDelay: `${index * 60}ms` }}
                                    onClick={() => handleActionClick(action)}
                                    data-testid={`quick-action-${action.id}`}
                                >
                                    <div className={`w-14 h-14 rounded-2xl ${style.iconBg} flex items-center justify-center ${style.iconGlow}`}>
                                        <action.icon className="w-7 h-7 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <Heading as="h3" size="text-lg" className="text-[#1D6B6B]">{action.title}</Heading>
                                        <BodyText className="text-[#7D8E95] truncate">{action.description}</BodyText>
                                    </div>
                                    <button
                                        onClick={(e) => handleFavoriteToggle(e, action.path)}
                                        className={`p-2 rounded-full transition-all duration-200 ${
                                            isFavorite(action.path)
                                                ? 'text-[#E8B931] hover:bg-[#E8B931]/10'
                                                : 'text-[#D0D0CD] hover:text-[#7D8E95] hover:bg-white/50'
                                        }`}
                                        data-testid={`favorite-${action.id}`}
                                        aria-label={isFavorite(action.path) ? 'Remove from favorites' : 'Add to favorites'}
                                    >
                                        <Star className={`w-5 h-5 ${isFavorite(action.path) ? 'fill-current' : ''}`} />
                                    </button>
                                    <ChevronRight className={`w-5 h-5 ${style.accent} transition-transform duration-200 group-hover:translate-x-1`} />
                                </div>
                            );
                        })}
                        
                        {/* Invite Partner Button - Glass-morphism Design */}
                        {activeTab === 'relationships' && (
                            <div className="mt-6 pt-5 border-t border-white/40/50">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#9A8A7A]"></div>
                                    <SmallText className="text-[#7D8E95] font-medium uppercase tracking-wide text-xs">Connect Together</SmallText>
                                </div>
                                <button
                                    onClick={() => navigate('/partner/invite')}
                                    className="w-full rounded-2xl glass-card p-5 flex items-center gap-4 border-2 border-dashed border-[#7D8E95]/40 hover:border-white/40/60 card-hover group"
                                    data-testid="invite-partner-btn"
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7D8E95] to-[#3A9595] flex items-center justify-center shadow-soft group-hover:from-[#FFBB98] group-hover:to-[#E09060] transition-all duration-300">
                                        <UserPlus className="w-7 h-7 text-white" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <Heading as="h3" size="text-lg" className="text-[#1D6B6B] group-hover:text-[#FFBB98] transition-colors">Invite Your Partner</Heading>
                                        <BodyText className="text-[#7D8E95]">Heal and grow together, not alone</BodyText>
                                    </div>
                                    <ChevronRight className="w-6 h-6 text-[#7D8E95] group-hover:text-[#FFBB98] group-hover:translate-x-1 transition-all duration-200" />
                                </button>
                            </div>
                        )}
                    </div>
                </section>

                {/* Progress Card - Streak & Weekly Progress */}
                <ProgressCard className="mb-8" />

                {/* Recommended For You - Personalized based on assessment */}
                <RecommendedForYou className="mb-8" />

                {/* Understanding ADHD - Link to educational content page */}
                <section className="mb-10">
                    <button
                        onClick={() => navigate('/understanding-adhd')}
                        className="w-full glass-card-lavender rounded-2xl p-5 text-left card-hover group"
                        data-testid="understanding-adhd-link"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1D6B6B] to-[#2A8585] flex items-center justify-center icon-glow-lavender flex-shrink-0">
                                <BookOpen className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <Heading as="h3" size="text-base" className="text-[#1D6B6B] group-hover:text-[#7D8E95] transition-colors">
                                    Understanding ADHD
                                </Heading>
                                <BodyText className="text-[#7D8E95] text-sm">
                                    Learn about the impact of ADHD on you and your relationships
                                </BodyText>
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#C0B0A0] group-hover:text-[#7D8E95] group-hover:translate-x-1 transition-all duration-200 flex-shrink-0" />
                        </div>
                    </button>
                </section>

                {/* Favorites Section - Glass-morphism */}
                {getFavoritesWithMetadata().length > 0 && (
                    <section className="mb-10">
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center icon-glow-gold">
                                    <Star className="w-4 h-4 text-[#D97706] fill-current" />
                                </div>
                                <div>
                                    <Heading as="h3" size="text-base" className="text-[#1D6B6B]">Your Favorites</Heading>
                                    <SmallText className="text-[#7D8E95]">Quick access to tools you love</SmallText>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {getFavoritesWithMetadata().slice(0, 4).map((tool, index) => {
                                const variantGlassClasses = {
                                    sage: 'glass-card-sage',
                                    rose: 'glass-card-rose',
                                    lavender: 'glass-card-lavender',
                                    blue: 'glass-card-blue',
                                    gold: 'glass-card-gold'
                                };
                                const variantIconColors = {
                                    sage: 'text-[#1D6B6B]',
                                    rose: 'text-[#FFBB98]',
                                    lavender: 'text-[#7D8E95]',
                                    blue: 'text-[#7D8E95]',
                                    gold: 'text-[#FFBB98]'
                                };
                                // Map icon string to component
                                const iconMap = { Heart, Sparkles, Compass, Wind, Zap, MessageCircle, Brain, ClipboardCheck, Users, RefreshCw, HeartHandshake };
                                const ToolIcon = iconMap[tool.icon] || Heart;
                                
                                return (
                                    <button
                                        key={tool.path}
                                        onClick={() => { trackGlobalToolUsage(tool.path); navigate(tool.path); }}
                                        className={`${variantGlassClasses[tool.variant] || variantGlassClasses.sage} rounded-2xl p-4 text-left card-hover brand-slide-up`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        data-testid={`favorite-tool-${index}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-10 h-10 rounded-xl bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-soft-sm ${variantIconColors[tool.variant] || variantIconColors.sage}`}>
                                                <ToolIcon className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-[#1D6B6B] text-sm mb-0.5">{tool.title}</p>
                                                <p className="text-xs text-[#7D8E95] line-clamp-2">{tool.description}</p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Recent Tools Section - Glass-morphism */}
                {recentTools.length > 0 && (
                    <section className="mb-10">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-8 h-8 rounded-lg bg-[#FFBB98] flex items-center justify-center">
                                <Clock className="w-4 h-4 text-[#7D8E95]" />
                            </div>
                            <div>
                                <Heading as="h3" size="text-base" className="text-[#1D6B6B]">Continue Where You Left Off</Heading>
                                <SmallText className="text-[#7D8E95]">Your recently used tools</SmallText>
                            </div>
                        </div>
                        <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-hide">
                            {recentTools.map((path, index) => {
                                const tool = GLOBAL_TOOL_METADATA[path];
                                if (!tool) return null;
                                const ToolIcon = tool.icon;
                                const variantBgColors = {
                                    sage: 'bg-gradient-to-br from-[#7D8E95]/20 to-[#7D8E95]/10',
                                    rose: 'bg-gradient-to-br from-[#FFBB98]/20 to-[#FFBB98]/10',
                                    lavender: 'bg-gradient-to-br from-[#7D8E95]/20 to-[#7D8E95]/10',
                                    blue: 'bg-gradient-to-br from-[#7D8E95]/20 to-[#7D8E95]/10',
                                    gold: 'bg-gradient-to-br from-[#FFBB98]/20 to-[#FFBB98]/10'
                                };
                                const variantIconColors = {
                                    sage: 'text-[#1D6B6B]',
                                    rose: 'text-[#FFBB98]',
                                    lavender: 'text-[#7D8E95]',
                                    blue: 'text-[#7D8E95]',
                                    gold: 'text-[#FFBB98]'
                                };
                                return (
                                    <button
                                        key={path}
                                        onClick={() => handleRecentToolClick(path)}
                                        className="flex-shrink-0 flex items-center gap-3 px-5 py-3.5 rounded-2xl glass-card card-hover brand-slide-up"
                                        style={{ animationDelay: `${index * 60}ms` }}
                                        data-testid={`recent-tool-${index}`}
                                    >
                                        <div className={`w-10 h-10 rounded-xl ${variantBgColors[tool.variant]} flex items-center justify-center`}>
                                            <ToolIcon className={`w-5 h-5 ${variantIconColors[tool.variant]}`} />
                                        </div>
                                        <div className="text-left">
                                            <BodyText className="font-semibold text-[#1D6B6B] whitespace-nowrap">{tool.title}</BodyText>
                                            <SmallText className="text-[#7D8E95]">Resume</SmallText>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-[#C0C0BD] ml-2" />
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Recent Sessions - Glass-morphism */}
                {recentSessions.length > 0 && (
                    <section className="mb-10">
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-[#E5EDE8] flex items-center justify-center">
                                    <History className="w-4 h-4 text-[#1D6B6B]" />
                                </div>
                                <div>
                                    <Heading as="h3" size="text-base" className="text-[#1D6B6B]">Recent Sessions</Heading>
                                    <SmallText className="text-[#7D8E95]">Your regulation journey</SmallText>
                                </div>
                            </div>
                            <button onClick={() => navigate('/history')} className="text-sm text-[#1D6B6B] hover:text-[#145555] font-medium flex items-center gap-1 transition-colors" data-testid="view-all-history-btn">
                                View all
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="space-y-3">
                            {recentSessions.map((session, index) => (
                                <div 
                                    key={session.id} 
                                    className="glass-card rounded-2xl p-4 card-hover brand-slide-up"
                                    style={{ animationDelay: `${index * 60}ms` }}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7D8E95]/20 to-[#7D8E95]/10 flex items-center justify-center">
                                                <Heart className="w-5 h-5 text-[#1D6B6B]" />
                                            </div>
                                            <div>
                                                <BodyText className="font-semibold text-[#1D6B6B]">{formatOverwhelmType(session.overwhelm_type)}</BodyText>
                                                <SmallText className="text-[#7D8E95]">{session.actions_completed} actions completed</SmallText>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <SmallText className="text-[#7D8E95] font-medium">{formatDate(session.created_at)}</SmallText>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Empty state - Glass-morphism */}
                {(!stats || stats.total_sessions === 0) && (
                    <section className="text-center py-16">
                        <div className="glass-card-sage w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center">
                            <Heart className="w-10 h-10 text-[#1D6B6B]" />
                        </div>
                        <Heading as="h3" size="text-xl" className="mb-3">Ready when you are</Heading>
                        <BodyText className="mb-8 max-w-sm mx-auto">
                            Start your first regulation session whenever you need it. There's no pressure here.
                        </BodyText>
                        <PrimaryButton onClick={() => navigate('/regulate')} size="xl" data-testid="start-first-session-btn">
                            Begin Regulating
                        </PrimaryButton>
                    </section>
                )}
            </main>

            {/* Onboarding Tour */}
            {showTour && (
                <OnboardingTour 
                    onComplete={() => {
                        setShowTour(false);
                        completeTour();
                    }} 
                />
            )}
        </div>
    );
};

export default UnifiedHome;
